import React, { useState } from 'react';
import { observer } from 'mobx-react';

import BaseDialog from 'core/components/base-dialog';
import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { TextField } from 'core/components/text-field';
import { useToast } from 'core/components/toast';
import { useStore } from 'core/store';
import { createGuestPaymentLink } from './guest-payment-link.service';
import useStyles from './guest-payment-link.styles';

const formatExpiry = (expiresAt) =>
  new Date(expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

/**
 * Staff "Create Payment Link" billing-tab action (#36685): a 30-day link to the account site's /pay
 * page with this policy already found, for an insured or mortgage lender to pay any amount without
 * signing in. Staff read it out, text it or email it themselves; nothing is sent from here.
 */
const GuestPaymentLink = observer(function GuestPaymentLink({ className }) {
  const classes = useStyles();
  const toast = useToast();
  const {
    account: {
      policies: {
        policy: { policy }
      }
    }
  } = useStore();

  const [link, setLink] = useState(null);
  const [creating, setCreating] = useState(false);

  const createLink = async () => {
    setCreating(true);
    try {
      setLink(await createGuestPaymentLink(policy.id));
    } catch (error) {
      toast.notify({ type: 'error', message: `Could not create the payment link. ${error.message}` });
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(link.url);
    toast.notify({ type: 'success', message: 'Payment link copied.' });
  };

  return (
    <>
      {/* A text link under "Add a payment hold", like Send Invoice; the caller passes that class. */}
      <Button variant="text" color="secondary" className={className} onClick={createLink} disabled={creating}>
        Create Payment Link
      </Button>
      <BaseDialog open={Boolean(link)} onClose={() => setLink(null)} className={classes.dialog}>
        {link && (
          <div className={classes.content}>
            <Label type="h1" className={classes.title}>
              Payment link
            </Label>
            <Label type="body3" className={classes.subtitle}>
              Anyone with this link can see the balance and pay it, without signing in, until{' '}
              {formatExpiry(link.expiresAt)}. Send it only to the insured or whoever pays their bill.
            </Label>
            <TextField
              id="guest-payment-link-url"
              label="Link"
              mode="light"
              value={link.url}
              readOnly
              className={classes.link}
            />
            <div className={classes.actions}>
              <Button variant="contained" color="secondary" onClick={copyLink}>
                Copy link
              </Button>
            </div>
          </div>
        )}
      </BaseDialog>
    </>
  );
});

export default GuestPaymentLink;
